// @ts-check
/** @typedef {import('../lib/schemas').ResolvedImage} ResolvedImage */
// Client-safe helper (task #67 follow-up). Imports NOTHING manifest-backed, so
// it is safe to use in client components and CSS-in-JS.
//
// normalizeOffers() (lib/normalizeOffers) turns backend offer image fields
// (imageUrl, imageOverride, fullSetImage, galleryImages[], fullSetGalleryImages[],
// category.pictogram) from plain URL strings into ResolvedImage descriptor
// OBJECTS { src, srcSet, width, height, ... }. Any consumer that still feeds one
// of those fields straight into an <img src=> or a CSS url(...) would stringify
// the object to "[object Object]" -> a bad relative URL -> 404 -> broken image
// (the regression this helper guards against).
//
// toSrc() collapses either shape to a URL string:
//   - a ResolvedImage descriptor object -> its `.src`
//   - a legacy plain string             -> itself (unchanged)
//   - anything falsy / unexpected       -> undefined
//
// Use it at the LAST step before a raw src/url sink:
//   <img src={toSrc(item.imageUrl)} />
//   background-image: url(${toSrc(props.imageUrl)});
// Do NOT use it where a component already accepts the full descriptor
// (OptimizedImage/ProgressiveImage image={...}) — that path wants the object.
/**
 * @param {ResolvedImage|string|null|undefined} value
 * @returns {string|undefined}
 */
export function toSrc(value) {
  if (!value) return undefined
  if (typeof value === 'string') return value
  if (typeof value === 'object') return value.src || undefined
  return undefined
}

// Normalize one image-ish value into a gallery ENTRY object that carousels /
// grids consume via `.src` (and, for descriptors, `.srcSet`/dims/placeholder).
//   - a ResolvedImage descriptor object -> returned as-is (already { src, ... })
//   - a legacy plain string             -> wrapped as { src: <string> }
//   - anything falsy / unexpected       -> null
// Fixes the `[{ src: item.imageUrl }]` idiom, which double-wrapped a descriptor
// object into { src: <descriptor> } -> `.src` stringified to "[object Object]".
/**
 * @param {ResolvedImage|string|null|undefined} value
 * @returns {ResolvedImage|{src: string}|null}
 */
export function toImageEntry(value) {
  if (!value) return null
  if (typeof value === 'string') return { src: value }
  if (typeof value === 'object') return value
  return null
}

export default toSrc
